import { Icon } from "@app/icons";
import { Button } from "@mantine/core";
import { notifyError } from "@ui/notify";
import { type MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { downloadExcel, type FileKind, openPrint, type TableView, tablePlace } from "./files";

/** A table's Excel and Print buttons: the table as shown (``viewOf``, read once the button shows it is busy) to an
 *  .xlsx file or to pages to print in a new tab, named after the page, ``title`` (else the dialog's) and the day.
 *  ``rows``: how many it has (a printout of too many is refused at once); ``details``: what the table belongs to (the
 *  page's top part in words), on top of both; the ``…Help`` texts are the tooltips. */
export function FileButtons(props: {
  viewOf: (kind: FileKind) => TableView | null;
  rows: number;
  title?: string;
  details?: string[];
  size: "compact-sm" | "compact-xs";
  excelHelp: string;
  printHelp: string;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState<FileKind | null>(null);
  const icon = props.size === "compact-sm" ? 15 : 13;
  const run = (kind: FileKind, e: MouseEvent<HTMLElement>) => {
    const place = { ...tablePlace(e.currentTarget, props.title), details: props.details };
    setBusy(kind);
    const made = kind === "excel" ? downloadExcel(props.viewOf, place) : openPrint(props.viewOf, place, props.rows);
    made.catch(notifyError).finally(() => setBusy(null));
  };
  return (
    <>
      <Button
        size={props.size}
        variant="default"
        leftSection={<Icon name="download" size={icon} />}
        loading={busy === "excel"}
        title={props.excelHelp}
        onClick={(e) => run("excel", e)}
      >
        {t("dataTable.excel")}
      </Button>
      <Button
        size={props.size}
        variant="default"
        leftSection={<Icon name="printer" size={icon} />}
        loading={busy === "print"}
        title={props.printHelp}
        onClick={(e) => run("print", e)}
      >
        {t("dataTable.print")}
      </Button>
    </>
  );
}
